/**
 * Invoices List Page - View all generated invoices
 * Features stats cards, filters, search, and invoice table
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  makeStyles,
  Text,
  Button,
  Input,
  Badge,
  Tooltip,
  tokens,
} from '@fluentui/react-components';
import {
  SearchRegular,
  ReceiptRegular,
  CheckmarkCircleRegular,
  ClockRegular,
  WarningRegular,
  MoneyRegular,
  EyeRegular,
  PrintRegular,
  ArrowLeftRegular,
  AddRegular,
  DismissRegular,
} from '@fluentui/react-icons';
import { ContentCard } from '../../../components/layout';
import { SearchableDropdown, TablePagination } from '../../../components/common';
import { usePagination } from '../../../hooks/usePagination';
import { ApiClient, newToast } from '../../../configurations/index.config';
import { getErrorMessage } from '../../../types';

const useStyles = makeStyles({
  container: {
    padding: '24px',
  },
  pageHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '24px',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  headerIcon: {
    fontSize: '32px',
    color: tokens.colorBrandForeground1,
  },
  headerText: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  pageTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#323130',
  },
  pageSubtitle: {
    fontSize: '14px',
    color: '#605e5c',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    gap: '16px',
    marginBottom: '24px',
  },
  statCard: {
    padding: '20px',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    border: '1px solid #e1dfdd',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  statIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
  },
  statContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  statValue: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#323130',
  },
  statLabel: {
    fontSize: '13px',
    color: '#605e5c',
  },
  filterSection: {
    marginBottom: '24px',
  },
  filterGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 200px 150px 150px auto auto',
    gap: '12px',
    alignItems: 'end',
  },
  searchBar: {
    marginBottom: '16px',
    maxWidth: '400px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  tableHeader: {
    backgroundColor: '#f5f5f5',
    borderBottom: '2px solid #e1dfdd',
  },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    fontSize: '13px',
    fontWeight: '600',
    color: '#323130',
  },
  thRight: {
    padding: '12px 16px',
    textAlign: 'right',
    fontSize: '13px',
    fontWeight: '600',
    color: '#323130',
  },
  tr: {
    borderBottom: '1px solid #e1dfdd',
    ':hover': {
      backgroundColor: '#f9f9f9',
    },
  },
  td: {
    padding: '12px 16px',
    fontSize: '14px',
    color: '#323130',
  },
  tdRight: {
    padding: '12px 16px',
    fontSize: '14px',
    color: '#323130',
    textAlign: 'right',
  },
  emptyState: {
    padding: '60px 20px',
    textAlign: 'center',
    color: '#605e5c',
  },
  actionButtons: {
    display: 'flex',
    gap: '8px',
  },
  clientInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  clientName: {
    fontWeight: '500',
  },
  clientEmail: {
    fontSize: '12px',
    color: '#605e5c',
  },
});

interface Invoice {
  id: number;
  invoice_number: string;
  client_id: number;
  client_name: string;
  client_email?: string;
  subtotal: number;
  tax_amount: number;
  total_amount: number;
  status: 'pending' | 'paid' | 'overdue' | 'cancelled';
  issue_date: string;
  due_date: string;
  paid_date?: string;
  created_at: string;
}

interface Stats {
  total: number;
  paid: number;
  pending: number;
  overdue: number;
  totalRevenue: number;
}

interface Client {
  id: number;
  client_name: string;
}

export const InvoicesListFluentUI: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<Stats>({
    total: 0,
    paid: 0,
    pending: 0,
    overdue: 0,
    totalRevenue: 0,
  });

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterClient, setFilterClient] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');

  useEffect(() => {
    fetchInvoices();
    fetchClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const response = await ApiClient.get('/get-invoices');
      if (response.data.status === 'success') {
        const data = response.data.data || [];
        setInvoices(data);
        calculateStats(data);
      } else {
        newToast('error', response.data.message || 'Failed to fetch invoices');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setLoading(false);
  };

  const fetchClients = async () => {
    try {
      const response = await ApiClient.get('/get-clients');
      if (response.data.status === 'success') {
        setClients(response.data.data || []);
      }
    } catch (error: unknown) {
      console.error('Error fetching clients:', error);
    }
  };

  const calculateStats = (data: Invoice[]) => {
    const paid = data.filter(inv => inv.status === 'paid');
    const pending = data.filter(inv => inv.status === 'pending');
    const overdue = data.filter(inv => inv.status === 'overdue');
    const totalRevenue = paid.reduce((sum, inv) => sum + inv.total_amount, 0);

    setStats({
      total: data.length,
      paid: paid.length,
      pending: pending.length,
      overdue: overdue.length,
      totalRevenue,
    });
  };

  const filteredInvoices = useMemo(() => {
    let filtered = [...invoices];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        inv =>
          inv.invoice_number?.toLowerCase().includes(query) ||
          inv.client_name?.toLowerCase().includes(query)
      );
    }

    // Status filter
    if (filterStatus && filterStatus !== 'all') {
      filtered = filtered.filter(inv => inv.status === filterStatus);
    }

    // Client filter
    if (filterClient) {
      filtered = filtered.filter(inv => inv.client_id.toString() === filterClient);
    }

    // Date range filter
    if (filterStartDate) {
      filtered = filtered.filter(inv => new Date(inv.issue_date) >= new Date(filterStartDate));
    }
    if (filterEndDate) {
      filtered = filtered.filter(inv => new Date(inv.issue_date) <= new Date(filterEndDate));
    }

    return filtered;
  }, [invoices, searchQuery, filterStatus, filterClient, filterStartDate, filterEndDate]);

  const { paginatedData, currentPage, itemsPerPage, totalItems, setCurrentPage, setItemsPerPage } =
    usePagination<Invoice>({ data: filteredInvoices, initialItemsPerPage: 10 });

  const clearFilters = () => {
    setSearchQuery('');
    setFilterStatus('all');
    setFilterClient('');
    setFilterStartDate('');
    setFilterEndDate('');
  };

  const hasActiveFilters =
    searchQuery !== '' ||
    filterStatus !== 'all' ||
    filterClient !== '' ||
    filterStartDate !== '' ||
    filterEndDate !== '';

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GH', {
      style: 'currency',
      currency: 'GHS',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) {
      return '—';
    }
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'paid':
        return (
          <Badge appearance="filled" color="success">
            Paid
          </Badge>
        );
      case 'pending':
        return (
          <Badge appearance="filled" color="warning">
            Pending
          </Badge>
        );
      case 'overdue':
        return (
          <Badge appearance="filled" color="danger">
            Overdue
          </Badge>
        );
      case 'cancelled':
        return (
          <Badge appearance="outline" color="subtle">
            Cancelled
          </Badge>
        );
      default:
        return (
          <Badge appearance="outline" color="informative">
            {status}
          </Badge>
        );
    }
  };

  const handleViewInvoice = (invoice: Invoice) => {
    navigate(`/account/billing/invoice-preview/${invoice.id}`);
  };

  const handleMarkAsPaid = async (invoice: Invoice) => {
    try {
      const response = await ApiClient.post('/update-invoice-status', {
        id: invoice.id,
        status: 'paid',
      });
      if (response.data.status === 'success') {
        newToast('success', 'Invoice marked as paid');
        fetchInvoices();
      } else {
        newToast('error', response.data.message || 'Failed to update invoice');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
  };

  return (
    <div className={styles.container}>
      {/* Page Header */}
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <Button
            appearance="subtle"
            icon={<ArrowLeftRegular />}
            onClick={() => navigate('/account/billing/generate-bill')}
          />
          <ReceiptRegular className={styles.headerIcon} />
          <div className={styles.headerText}>
            <Text className={styles.pageTitle}>Invoices</Text>
            <Text className={styles.pageSubtitle}>View and manage all generated invoices</Text>
          </div>
        </div>
        <Button
          appearance="primary"
          icon={<AddRegular />}
          onClick={() => navigate('/account/billing/generate-bill')}
        >
          Generate New Invoice
        </Button>
      </div>

      {/* Stats Cards */}
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ backgroundColor: '#e8f4fd', color: '#0078d4' }}>
            <ReceiptRegular />
          </div>
          <div className={styles.statContent}>
            <Text className={styles.statValue}>{stats.total}</Text>
            <Text className={styles.statLabel}>Total Invoices</Text>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ backgroundColor: '#dff6dd', color: '#107c10' }}>
            <CheckmarkCircleRegular />
          </div>
          <div className={styles.statContent}>
            <Text className={styles.statValue}>{stats.paid}</Text>
            <Text className={styles.statLabel}>Paid</Text>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ backgroundColor: '#fff4ce', color: '#ffaa44' }}>
            <ClockRegular />
          </div>
          <div className={styles.statContent}>
            <Text className={styles.statValue}>{stats.pending}</Text>
            <Text className={styles.statLabel}>Pending</Text>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ backgroundColor: '#fde7e9', color: '#d13438' }}>
            <WarningRegular />
          </div>
          <div className={styles.statContent}>
            <Text className={styles.statValue}>{stats.overdue}</Text>
            <Text className={styles.statLabel}>Overdue</Text>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ backgroundColor: '#e8f4fd', color: '#0078d4' }}>
            <MoneyRegular />
          </div>
          <div className={styles.statContent}>
            <Text className={styles.statValue}>{formatCurrency(stats.totalRevenue)}</Text>
            <Text className={styles.statLabel}>Total Revenue</Text>
          </div>
        </div>
      </div>

      {/* Filters */}
      <ContentCard>
        <div className={styles.filterSection}>
          <div className={styles.filterGrid}>
            <Input
              placeholder="Search invoice # or client..."
              contentBefore={<SearchRegular />}
              value={searchQuery}
              onChange={(e, data) => setSearchQuery(data.value)}
            />

            <SearchableDropdown
              placeholder="All Clients"
              options={[
                { id: '', label: 'All Clients' },
                ...clients.map(c => ({ id: c.id.toString(), label: c.client_name })),
              ]}
              selectedValue={filterClient}
              onSelect={setFilterClient}
            />

            <SearchableDropdown
              placeholder="Status"
              options={[
                { id: 'all', label: 'All Status' },
                { id: 'paid', label: 'Paid' },
                { id: 'pending', label: 'Pending' },
                { id: 'overdue', label: 'Overdue' },
              ]}
              selectedValue={filterStatus}
              onSelect={setFilterStatus}
            />

            <Input
              type="date"
              value={filterStartDate}
              onChange={(e, data) => setFilterStartDate(data.value)}
              placeholder="From"
            />

            <Input
              type="date"
              value={filterEndDate}
              onChange={(e, data) => setFilterEndDate(data.value)}
              placeholder="To"
            />

            {hasActiveFilters && (
              <Button appearance="subtle" icon={<DismissRegular />} onClick={clearFilters}>
                Clear
              </Button>
            )}
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className={styles.emptyState}>
            <Text>Loading invoices...</Text>
          </div>
        ) : filteredInvoices.length === 0 ? (
          <div className={styles.emptyState}>
            <ReceiptRegular style={{ fontSize: '48px', color: '#c8c6c4', marginBottom: '16px' }} />
            <Text size={500} weight="semibold" style={{ display: 'block' }}>
              No Invoices Found
            </Text>
            <Text style={{ marginTop: '8px', display: 'block' }}>
              {hasActiveFilters
                ? 'Try adjusting your filters'
                : 'Generate your first invoice to get started'}
            </Text>
          </div>
        ) : (
          <>
            <table className={styles.table}>
              <thead className={styles.tableHeader}>
                <tr>
                  <th className={styles.th}>Invoice #</th>
                  <th className={styles.th}>Client</th>
                  <th className={styles.th}>Issue Date</th>
                  <th className={styles.th}>Due Date</th>
                  <th className={styles.thRight}>Amount</th>
                  <th className={styles.th}>Status</th>
                  <th className={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((invoice: Invoice) => (
                  <tr key={invoice.id} className={styles.tr}>
                    <td className={styles.td}>
                      <Text weight="semibold">{invoice.invoice_number}</Text>
                    </td>
                    <td className={styles.td}>
                      <div className={styles.clientInfo}>
                        <span className={styles.clientName}>{invoice.client_name}</span>
                        {invoice.client_email && (
                          <span className={styles.clientEmail}>{invoice.client_email}</span>
                        )}
                      </div>
                    </td>
                    <td className={styles.td}>{formatDate(invoice.issue_date)}</td>
                    <td className={styles.td}>{formatDate(invoice.due_date)}</td>
                    <td className={styles.tdRight}>
                      <Text weight="semibold">{formatCurrency(invoice.total_amount)}</Text>
                    </td>
                    <td className={styles.td}>{getStatusBadge(invoice.status)}</td>
                    <td className={styles.td}>
                      <div className={styles.actionButtons}>
                        <Tooltip content="View Invoice" relationship="label">
                          <Button
                            appearance="subtle"
                            icon={<EyeRegular />}
                            size="small"
                            onClick={() => handleViewInvoice(invoice)}
                          />
                        </Tooltip>
                        <Tooltip content="Print" relationship="label">
                          <Button
                            appearance="subtle"
                            icon={<PrintRegular />}
                            size="small"
                            onClick={() => handleViewInvoice(invoice)}
                          />
                        </Tooltip>
                        {invoice.status === 'pending' && (
                          <Tooltip content="Mark as Paid" relationship="label">
                            <Button
                              appearance="subtle"
                              icon={<CheckmarkCircleRegular />}
                              size="small"
                              onClick={() => handleMarkAsPaid(invoice)}
                            />
                          </Tooltip>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <TablePagination
              totalItems={totalItems}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
            />
          </>
        )}
      </ContentCard>
    </div>
  );
};

export default InvoicesListFluentUI;
